import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../../utils/functions';

const normalizeMetricValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) && parsedValue >= 0
        ? parsedValue
        : null;
};

const buildExpectedStatsFromScreens = (selectedScreens = [], expectedStats = {}) =>
    selectedScreens.reduce((accumulator, screen) => {
        const screenId = String(screen?.id ?? '').trim();
        if (!screenId) {
            return accumulator;
        }

        const currentTarget = expectedStats?.[screenId] || {};
        accumulator[screenId] = {
            impressions: normalizeMetricValue(currentTarget.impressions),
            impacts: normalizeMetricValue(currentTarget.impacts),
            spent: normalizeMetricValue(currentTarget.spent),
        };
        return accumulator;
    }, {});

const INPUT_FIELDS = [
    {
        key: 'impressions',
        step: '1',
        labelKey:
            'sections.campaigns.new-campaign.wizard-expected-stats.columns.impressions',
    },
    {
        key: 'impacts',
        step: '1',
        labelKey:
            'sections.campaigns.new-campaign.wizard-expected-stats.columns.impacts',
    },
    {
        key: 'spent',
        step: '0.01',
        labelKey:
            'sections.campaigns.new-campaign.wizard-expected-stats.columns.spent',
    },
];

const getScreenCostPerSpot = (screen, userMargin = 0) => {
    const rawPrice = Number(screen?.deals?.prices?.[0]?.price);
    if (!Number.isFinite(rawPrice)) {
        return null;
    }

    const normalizedPrice = rawPrice / 1000;
    const marginDivisor = 1 - Number(userMargin || 0);

    if (!Number.isFinite(marginDivisor) || marginDivisor <= 0) {
        return normalizedPrice;
    }

    return normalizedPrice / marginDivisor;
};

const getScreenWeeklyImpacts = (screen) => {
    const weeklyImpacts = Number(screen?.weeklyImpacts);
    return Number.isFinite(weeklyImpacts) ? weeklyImpacts : 0;
};

function WizardExpectedStats({
    isActive,
    selectedScreens,
    expectedStats,
    onChangeExpectedStats,
    selectedCurrencyCode,
    userMargin,
}) {
    const { t } = useTranslation();

    useEffect(() => {
        const normalizedTargets = buildExpectedStatsFromScreens(
            selectedScreens,
            expectedStats
        );

        if (
            JSON.stringify(normalizedTargets) !== JSON.stringify(expectedStats || {})
        ) {
            onChangeExpectedStats(normalizedTargets);
        }
    }, [expectedStats, onChangeExpectedStats, selectedScreens]);

    if (!isActive) {
        return null;
    }

    const handleChangeMetric = (screenId, metricKey, rawValue) => {
        const nextValue = normalizeMetricValue(String(rawValue ?? '').trim());

        onChangeExpectedStats({
            ...buildExpectedStatsFromScreens(selectedScreens, expectedStats),
            [screenId]: {
                ...expectedStats?.[screenId],
                [metricKey]: nextValue,
            },
        });
    };

    return (
        <div className="basics mt-2 h-full overflow-visible overflow-y-auto text-sm transition-transform duration-300">
            <div className="module rounded m-1 mb-4">
                <div className="header flex justify-between content-center ml-1">
                    <div className="title text-xl font-extralight">
                        {t(
                            'sections.campaigns.new-campaign.wizard-expected-stats.title'
                        )}
                    </div>
                    <div className="brief font-light text-xs text-gray-400 mt-1.5 text-right max-w-xl">
                        {t(
                            'sections.campaigns.new-campaign.wizard-expected-stats.description'
                        )}
                    </div>
                </div>

                <div className="body rounded-sm p-1">
                    {selectedScreens.length === 0 ? (
                        <div className="m-1 rounded border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-sm text-gray-500">
                            {t(
                                'sections.campaigns.new-campaign.wizard-expected-stats.empty'
                            )}
                        </div>
                    ) : (
                        <div className="space-y-3 px-1 pb-4">
                            {selectedScreens.map((screen) => {
                                const screenId = String(screen?.id ?? '').trim();
                                const target = expectedStats?.[screenId] || {};
                                const weeklyImpacts =
                                    getScreenWeeklyImpacts(screen);
                                const costPerSpot = getScreenCostPerSpot(
                                    screen,
                                    userMargin
                                );

                                return (
                                    <div
                                        key={screenId}
                                        className="rounded border border-gray-200 bg-white shadow-sm">
                                        <div className="flex flex-col gap-3 border-b border-gray-100 bg-gray-50 px-4 py-3 lg:flex-row lg:items-start lg:justify-between">
                                            <div className="min-w-0">
                                                <div className="font-medium text-gray-900">
                                                    {screen?.name ||
                                                        screen?.alias ||
                                                        '-'}
                                                </div>
                                                <div className="mt-1 text-xs text-gray-500">
                                                    ID {screenId}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 gap-2 text-xs text-gray-500 sm:grid-cols-2 lg:min-w-[360px]">
                                                <div className="rounded bg-white px-3 py-2 border border-gray-200">
                                                    <div className="uppercase tracking-wide text-[11px]">
                                                        {t(
                                                            'sections.campaigns.new-campaign.wizard-expected-stats.columns.weeklyImpacts'
                                                        )}
                                                    </div>
                                                    <div className="mt-1 text-sm font-medium text-gray-800">
                                                        {formatNumber(
                                                            weeklyImpacts,
                                                            'rounded'
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="rounded bg-white px-3 py-2 border border-gray-200">
                                                    <div className="uppercase tracking-wide text-[11px]">
                                                        {t(
                                                            'sections.campaigns.new-campaign.wizard-expected-stats.columns.costPerSpot'
                                                        )}
                                                    </div>
                                                    <div className="mt-1 text-sm font-medium text-gray-800">
                                                        {costPerSpot === null
                                                            ? '-'
                                                            : `${selectedCurrencyCode || ''} ${formatNumber(costPerSpot)}`}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 gap-3 px-4 py-4 md:grid-cols-3">
                                            {INPUT_FIELDS.map((field) => (
                                                <label
                                                    key={field.key}
                                                    className="block">
                                                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                        {t(field.labelKey)}
                                                    </div>
                                                    <div className="mt-1 relative rounded-md shadow-sm">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step={field.step}
                                                            value={
                                                                target?.[
                                                                    field.key
                                                                ] ?? ''
                                                            }
                                                            onChange={(event) =>
                                                                handleChangeMetric(
                                                                    screenId,
                                                                    field.key,
                                                                    event.target
                                                                        .value
                                                                )
                                                            }
                                                            className="relative shadow-sm w-full sm:text-sm rounded-md mt-1 outline-none transition-colors border-gray-300 focus:border-blue-400 focus:ring-0"
                                                            placeholder="0"
                                                        />
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default WizardExpectedStats;
